import { useState, useContext, useEffect, useMemo, useRef, createContext } from 'react'
import { TransitionGroup } from 'react-transition-group'
import { HexColorPicker } from "react-colorful"

import _ from "lodash"
import ShortUniqueId from 'short-unique-id'
import { flatten } from "flat"
import moment from 'moment-timezone'
import isEmail from 'validator/lib/isEmail'
import isURL from 'validator/lib/isURL'

import { ToggleButtonGroup, ToggleButton, Card, CardHeader, Slide, List, ListItem, Collapse, TextField, Typography, IconButton, Button, FormControlLabel, RadioGroup, Radio, Switch, Stack, Tooltip, CardContent, Paper, InputAdornment, MenuItem } from '@mui/material'
import EastRoundedIcon from '@mui/icons-material/EastRounded'
import WestRoundedIcon from '@mui/icons-material/WestRounded'
import ArrowCircleUpOutlinedIcon from '@mui/icons-material/ArrowCircleUpOutlined'
import ArrowCircleDownOutlinedIcon from '@mui/icons-material/ArrowCircleDownOutlined'
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined'
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined'
import NotificationAddIcon from '@mui/icons-material/NotificationAdd'
import AddBusinessIcon from '@mui/icons-material/AddBusiness'
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined'
import DatasetOutlinedIcon from '@mui/icons-material/DatasetOutlined'
import EventOutlinedIcon from '@mui/icons-material/EventOutlined'
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined'

import FormatAlignLeftIcon from '@mui/icons-material/FormatAlignLeft'
import FormatAlignCenterIcon from '@mui/icons-material/FormatAlignCenter'
import FormatAlignRightIcon from '@mui/icons-material/FormatAlignRight'

import { AppContext } from '/providers/app-context'
import { PassContext } from '../pass-context'
import dataSelector from './data-selector'
import currencies from '/utils/currencies.json'
import VariablesSelector from '/utils/variable-selector'
import getFormattedValue from './popup-editor-field-formatter'
import findPathByValue from '/utils/find-path-by-value'
import PopupShell from '../popup-shell'
import { fromNativeDateTimeValue, fromNativeDateValue, isValidDateInput, isValidDateTimeInput, isValidTimeInput, toNativeDateTimeValue, toNativeDateValue } from '/utils/date-time'

const EditorContext = createContext({})

const isIntegerInput = input => /^-?\d*$/.test(input)
const isDecimalInput = input => /^-?\d*(\.\d*)?$/.test(input)
const isValidCurrencyCodeInput = input => /^[A-Z]{3}$/.test(input ?? "")
  && currencies.some(currency => currency.code === input)
const isValidImageUrlInput = input => {
  if (!isURL(input, { protocols: ['http', 'https'], require_protocol: true })) return false

  try {
    const { pathname } = new URL(input)
    return /\.(png|jpe?g|gif|webp|svg|bmp|avif)$/i.test(pathname)
  }
  catch {
    return false
  }
}
const isValidColourInput = input => /^#?[0-9a-fA-F]{6}$/.test(input)
const normaliseColourInput = input => /^#[0-9a-fA-F]{6}$/.test(input)
  ? input
  : /^[0-9a-fA-F]{6}$/.test(input)
    ? `#${input}`
    : null
const isValidLocationInput = input => {
  if (typeof input !== "string") return false

  const parts = input.split(",").map(part => part.trim())
  if (parts.length !== 2 || parts.some(part => part === "")) return false

  const [latitude, longitude] = parts.map(Number)
  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) return false

  return latitude >= -90 && latitude <= 90 && longitude >= -180 && longitude <= 180
}

const validateValueInput = ({ input, varSubType }) => {
  const value = input ?? ""
  if (value === "") return { valid: true, helperText: "" }

  switch (varSubType) {
    case "email":
      return { valid: isEmail(value), helperText: isEmail(value) ? "" : "Enter a valid email address." }
    case "link":
      return {
        valid: isURL(value, { protocols: ['http', 'https'], require_protocol: true }),
        helperText: isURL(value, { protocols: ['http', 'https'], require_protocol: true }) ? "" : "Enter a valid URL starting with http:// or https://."
      }
    case "image":
      return {
        valid: isValidImageUrlInput(value),
        helperText: isValidImageUrlInput(value) ? "" : "Enter a valid http:// or https:// image URL ending in an image file extension."
      }
    case "number":
    case "money":
      return { valid: isDecimalInput(value), helperText: isDecimalInput(value) ? "" : "Enter a valid number." }
    case "date":
      return { valid: isValidDateInput(value), helperText: isValidDateInput(value) ? "" : "Use ISO format YYYY-MM-DD and enter a valid date." }
    case "time":
      return { valid: isValidTimeInput(value), helperText: isValidTimeInput(value) ? "" : "Use time format HH:mm:ss and enter a valid time." }
    case "datetime":
      return {
        valid: isValidDateTimeInput(value),
        helperText: isValidDateTimeInput(value) ? "" : "Use ISO format YYYY-MM-DDTHH:mm:ss±HH:MM and a valid date/time."
      }
    case "location":
      return {
        valid: isValidLocationInput(value),
        helperText: isValidLocationInput(value) ? "" : "Use latitude,longitude with valid ranges."
      }
    case "colour":
      return {
        valid: isValidColourInput(value),
        helperText: isValidColourInput(value) ? "" : "Use a 6-digit hex colour such as #A1B2C3."
      }
    case "choice":
      return {
        valid: isValidCurrencyCodeInput(value),
        helperText: isValidCurrencyCodeInput(value) ? "" : "Use a valid 3-letter ISO currency code such as GBP."
      }
    default:
      return { valid: true, helperText: "" }
  }
}

const applyFormattingDefaults = (fieldItem, value) => {
  fieldItem.type = value
  delete fieldItem.ignoresTimeZone
  delete fieldItem.isRelative
  delete fieldItem.dateStyle
  delete fieldItem.timeStyle
  delete fieldItem.numberStyle
  delete fieldItem.currencyCode

  switch (value) {
    case "number":
      if (!fieldItem.numberStyle) fieldItem.numberStyle = "PKNumberStyleDecimal"
      break

    case "date":
      if (!fieldItem.dateStyle) fieldItem.dateStyle = "PKDateStyleShort"
      if (fieldItem.isRelative === undefined) fieldItem.isRelative = false
      if (fieldItem.ignoresTimeZone === undefined) fieldItem.ignoresTimeZone = true
      break

    case "time":
      if (!fieldItem.timeStyle) fieldItem.timeStyle = "PKDateStyleShort"
      if (fieldItem.ignoresTimeZone === undefined) fieldItem.ignoresTimeZone = true
      break

    case "datetime":
      if (!fieldItem.dateStyle) fieldItem.dateStyle = "PKDateStyleShort"
      if (!fieldItem.timeStyle) fieldItem.timeStyle = "PKDateStyleShort"
      if (fieldItem.isRelative === undefined) fieldItem.isRelative = false
      if (fieldItem.ignoresTimeZone === undefined) fieldItem.ignoresTimeZone = true
      break

    case "money":
      if (!fieldItem.currencyCode) fieldItem.currencyCode = "GBP"
      // TODO - add a currecny code drop down here.
      break
  }
}

const applyFormattingPresetFromCatalog = (fieldItem, catalogEntry) => {
  const formatting = catalogEntry?.var_formatting ?? ({
    integer: "number",
    number: "number",
    money: "money",
    date: "date",
    time: "time",
    datetime: "datetime",
    text: "text",
    www: "www",
    phone: "phone",
    email: "email"
  }[catalogEntry?.var_type] ?? null)
  const formattingSubType = catalogEntry?.var_formatting_sub_type ?? catalogEntry?.var_sub_type ?? catalogEntry?.sub__type

  if (!formatting) return

  applyFormattingDefaults(fieldItem, formatting)
  switch (formatting) {
    case "number":
      if (formattingSubType) fieldItem.numberStyle = formattingSubType
      break

    case "date":
      if (formattingSubType) fieldItem.dateStyle = formattingSubType
      break

    case "time":
      if (formattingSubType) fieldItem.timeStyle = formattingSubType
      break

    case "datetime":
      if (formattingSubType) {
        fieldItem.dateStyle = formattingSubType
        fieldItem.timeStyle = formattingSubType
      }
      break

    case "money":
      if (formattingSubType) fieldItem.currencyCode = formattingSubType
      break
  }
}

const getCatalogPathFromReference = value => {
  if (typeof value !== "string") return ""

  const match = value.match(/^{{([^}]+)}}$/)
  return match?.[1] ?? ""
}

const appleFormattingTypes = ["text", "number", "date", "time", "datetime", "money"]
const appleLinkFieldTypes = ["www", "email", "phone", "location"]

const getAllowedAppleFormattingTypes = ({ item, catalogMap }) => {
  const variablePath = getCatalogPathFromReference(item.value)
  const variableType = catalogMap[variablePath]?.var_type

  switch (variableType) {
    case "date": return ["date"]
    case "time": return ["time"]
    case "datetime": return ["date", "time", "datetime"]
    case "www":
    case "email":
    case "phone":
    case "location":
      return [variableType]
    default:
      return [...appleFormattingTypes, ...appleLinkFieldTypes]
  }
}

function shortUUID() {
  const uid = new ShortUniqueId({ length: 12 })
  const uuid = uid.rnd()

  const uuidArray = uuid.match(/.{1,4}/g)
  const formattedUUID = uuidArray.join("-")

  return formattedUUID
}

const PopupDialogFields = ({ open, close, title, path, maxFields, colors }) => {

  const { client, selectedClientId } = useContext(AppContext)
  const { template, variables, setVariables, setTemplate, openVariablesManager, variablesCatalog = [], isManagedPassTemplate = false } = useContext(PassContext)

  const [fields, setFields] = useState([])
  const [newVariables, setNewVariables] = useState({})
  const [validationErrors, setValidationErrors] = useState({})
  const [validationTarget, setValidationTarget] = useState(null)
  const [sourcePaths, setSourcePaths] = useState([])
  const [view, setView] = useState("list")
  const catalogMap = useMemo(() => _.keyBy(variablesCatalog, "var_name"), [variablesCatalog])
  const hasValidationErrors = useMemo(() => Object.values(validationErrors).some(Boolean), [validationErrors])
  const hasInvalidFieldValues = useMemo(() => fields.some(item => {
    return ["value"].some(field => {
      const variablePath = getCatalogPathFromReference(item[field])
      const varSubType = variablePath ? catalogMap[variablePath]?.var_type ?? item.type : item.type
      if (!["date", "time", "datetime"].includes(varSubType)) return false

      const value = variablePath ? _.get(newVariables, variablePath) : item[field]
      return !validateValueInput({ input: value, varSubType }).valid
    })
  }), [catalogMap, fields, newVariables])

  const dataChanged = useMemo(() => {
    return !_.isEqual(fields, template[path]) || !_.isEqual(newVariables, variables)
  }, [fields, newVariables, variables, path, template])

  // Runs on "open" state change or if path changes
  // Ensures we have the current data rather than something left behind from previous form 
  useEffect(() => {
    if (open) {
      let f = _.cloneDeep(template[path] ?? [])

      // Set some default values for any entries we do have that might not have all options set
      f.map(i => {
        if (!i.type) i.type = "text"
        if (!i.textAlignment) i.textAlignment = "PKTextAlignmentLeft"
        if (!i.readonly) i.readonly = { label: false, value: false }

        if (i.dateStyle) {
          if (i.isRelative === undefined) i.isRelative = false
        }

        if (i.dateStyle || i.timeStyle) {
          if (i.ignoresTimeZone === undefined) i.ignoresTimeZone = true
        }
      })

      setFields(f)
      setNewVariables(_.cloneDeep(variables))
      setValidationErrors({})
      setView("list")
    }

  }, [open, path, template])

  useEffect(() => {
    if (!open) return
    setNewVariables(current => _.merge({}, variables, current))
  }, [open, variables])


  // Refresh our template / instance data source paths if instancedata changes
  useEffect(() => {
    setSourcePaths(Object.keys(flatten(newVariables)).map(p => `{{${p}}}`).sort())
  }, [newVariables])



  //////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Support Functions
  //////////////////////////////////////////////////////////////////////////////////////////////////////////

  const addItem = () => {

    const newFieldId = shortUUID()

    const newField = {
      key: newFieldId,
      label: "",
      value: "",
      type: "text",
      textAlignment: "PKTextAlignmentLeft"
    }

    setFields((prev) => [...prev, newField])
  }

  const addNotifications = () => {
    const latestNotificationField = {
      key: "smartix_latest_notification",
      label: "Notification",
      value: "[Latest notification will appear here]",
      type: "text",
      textAlignment: "PKTextAlignmentLeft",
      changeMessage: "%@",
      readonly: {
        label: false,
        value: true
      }
    }

    const notificationHistoryField = {
      key: "smartix_notifications",
      label: "Notification History",
      value: "[Older notifications will appear here]",
      type: "text",
      textAlignment: "PKTextAlignmentLeft",
      readonly: {
        label: false,
        value: false
      }
    }

    setFields(prev => {
      const hasLatest = prev.some(field => field.key === latestNotificationField.key)
      const hasHistory = prev.some(field => field.key === notificationHistoryField.key)
      const currentFields = prev.map(field => field.key === notificationHistoryField.key
        ? { ...field, readonly: { ...field.readonly, value: false } }
        : field)

      return [
        ...(hasLatest ? [] : [latestNotificationField]),
        ...currentFields,
        ...(hasHistory ? [] : [notificationHistoryField])
      ]
    })

  }

  const addAdvert = () => {
    const newField = {
      key: "smartix_advert",
      label: "Advert",
      value: "[Advert will appear here]",
      type: "text",
      textAlignment: "PKTextAlignmentLeft",
      readonly: {
        label: false,
        value: true
      }
    }

    setFields((prev) => [...prev, newField])
  }

  const resolveValue = (item, field) => {
    return dataSelector({ template: item, variables: newVariables, path: field })
  }

  const saveFormData = () => {
    if (hasInvalidFieldValues) return

    let nt = _.cloneDeep(template)
    nt[path] = fields

    // Save content of our "new" template/instance data back to state
    if (!_.isEqual(nt, template)) setTemplate(nt)
    if (!_.isEqual(newVariables, variables)) setVariables(newVariables)

    close()
  }

  const handleClose = (e, reason) => {
    if (reason && reason === "backdropClick")
      return
    close()
  }


  //////////////////////////////////////////////////////////////////////////////////////////////////////////
  // Main Render
  //////////////////////////////////////////////////////////////////////////////////////////////////////////

  const limitedEditing = (item, field) => (field === "value" && client.client_id !== 0 && item.key.match(/__SMARTIX/) !== null)
  const selectedClient = client?.available_clients?.find(item => String(item.client_id) === String(selectedClientId))
  const showDeveloperBackFieldActions = client.client_id === 0 && path === "backFields"
  const showAdvertBackFieldAction = path === "backFields" && Number(selectedClient?.parent_client_id) === 0
  const hasLatestNotificationField = fields.some(field => field.key === "smartix_latest_notification")
  const hasNotificationsField = fields.some(field => field.key === "smartix_notifications")
  const hasAdvertField = fields.some(field => field.key === "smartix_advert")

  return (
    <PopupShell open={open} onClose={handleClose}>

      <div className="flex flex-col p-2 h-full w-full overflow-x-hidden">

        <div className="flex flex-row justify-between mt-5 w-full">
          <div className="flex flex-row items-center gap-1">
            <IconButton color='primary' onClick={addItem} disabled={fields?.length >= maxFields || view !== "list"}><AddCircleOutlineOutlinedIcon /></IconButton>
            {showDeveloperBackFieldActions && <IconButton color='primary' onClick={addNotifications} disabled={fields?.length >= maxFields || view !== "list" || (hasLatestNotificationField && hasNotificationsField)}><NotificationAddIcon /></IconButton>}
            {showAdvertBackFieldAction && <IconButton color='primary' onClick={addAdvert} disabled={fields?.length >= maxFields || view !== "list" || hasAdvertField}><AddBusinessIcon /></IconButton>}
          </div>
          <Typography color="secondary" variant="h6">{title}</Typography>
          <Tooltip title="You can press ESC on your keyboard to dismiss"><Button endIcon={<EastRoundedIcon />} onClick={handleClose} >cancel</Button></Tooltip>
        </div>

        {colors?.label &&
          <div className="flex flex-col w-full mt-3">
            <div><b>label colour :</b> {colors.label}</div>
            <div><b>value colour :</b> {colors.value}</div>
          </div>
        }

        <hr />

        <EditorContext.Provider value={{ resolveValue, limitedEditing, fields, setFields, sourcePaths, variables: newVariables, setVariables: setNewVariables, dataSelector, path, catalogMap, validationErrors, setValidationErrors, isManagedPassTemplate, focusValidationField: setValidationTarget }}>
          <CardsContainer view={view} setView={setView} validationTarget={validationTarget} clearValidationTarget={() => setValidationTarget(null)} />
        </EditorContext.Provider>

        <div className='fixed bottom-5 w-[460px]'>

          <hr className='mb-5' />

          <div className="flex flex-row w-full justify-between">
            <Button startIcon={<DatasetOutlinedIcon />} onClick={openVariablesManager} >Manage variables</Button>
            <div className="flex flex-row items-center gap-3">
              {hasValidationErrors && <ValidationSummary validationErrors={validationErrors} onFocusField={setValidationTarget} />}
              <Button variant="contained" onClick={saveFormData} disabled={!dataChanged || hasValidationErrors || hasInvalidFieldValues}>Save</Button>
            </div>
          </div>
        </div>
      </div>
    </PopupShell>
  )
}

export default PopupDialogFields


//////////////////////////////////////////////////////////////////////////////////////////////////////////
// Controlled JSX (Must have values passes as paramets and not access them from the parent 
// - hence outside the main function). Otherwise they do not update when data changes!
//////////////////////////////////////////////////////////////////////////////////////////////////////////



const CardsContainer = ({ view, setView, validationTarget, clearValidationTarget }) => {

  const { fields } = useContext(EditorContext)
  const [selectedItem, setSelectedItem] = useState()
  const containerRef = useRef(null)

  useEffect(() => {
    const item = fields.filter(f => f.key === selectedItem?.key)[0]
    if (item) setSelectedItem(item)
  }, [fields])

  useEffect(() => {
    containerRef.current?.scrollTo({ top: 0 })
  }, [view, selectedItem?.key])

  useEffect(() => {
    if (!validationTarget) return
    const [itemKey] = validationTarget.split(':')
    const item = fields.find(field => field.key === itemKey)
    if (item) {
      setSelectedItem(item)
      setView('edit')
    }
    clearValidationTarget()
  }, [validationTarget, fields, setView, clearValidationTarget])

  return (
    <div ref={containerRef} className='mt-5 mb-[100px] relative w-full h-full overflow-y-scroll overflow-x-hidden'>
      <CardList view={view} setView={setView} setSelectedItem={setSelectedItem} />
      <EditCard selectedItem={selectedItem} view={view} setView={setView} />
    </div>
  )
}

const CardList = ({ view, setView, setSelectedItem }) => {

  const endRef = useRef(null)
  const { fields } = useContext(EditorContext)

  // track previous length
  const prevLengthRef = useRef(fields.length)

  useEffect(() => {
    // update refs if items removed
    if (fields.length < prevLengthRef.current) {
      prevLengthRef.current = fields.length
    }
  }, [fields])


  return (

    <Slide
      direction="right"
      in={view === "list"}
      timeout={400}
      mountOnEnter
      unmountOnExit
    >
      <div className='absolute top-0 left-0 w-full'>
        <List disablePadding dense >
          <TransitionGroup>
            {fields.map((item, idx) => (
              <Collapse key={item.key}
                onEntered={() => {
                  // only scroll if a new item was added
                  if (
                    idx === fields.length - 1 &&
                    fields.length > prevLengthRef.current
                  ) {
                    endRef.current?.scrollIntoView({
                      behavior: "smooth",
                      block: "end",
                    })
                  }
                  prevLengthRef.current = fields.length
                }}>
                <ItemCard
                  idx={idx}
                  item={item}
                  setView={setView}
                  value={item.value}
                  setSelectedItem={setSelectedItem}
                />
              </Collapse>
            ))}
          </TransitionGroup>
        </List>
        <div ref={endRef} />
      </div>
    </Slide>
  )
}


const ItemCard = ({ item, idx, setView, setSelectedItem }) => {

  const { resolveValue, fields, setFields } = useContext(EditorContext)

  const resolvedValue = resolveValue(item, "value") ?? ""
  const resolvedLabel = resolveValue(item, "label") ?? ""
  const resolvedCurrencyCode = item.type === "money"
    ? (resolveValue(item, "currencyCode") ?? item.currencyCode)
    : item.currencyCode
  const formatterItem = item.type === "money"
    ? { ...item, currencyCode: resolvedCurrencyCode }
    : item

  let title = resolvedLabel !== "" ? resolvedLabel : "[label not set]"
  let subtitle = getFormattedValue({ item: formatterItem, resolvedValue: resolvedValue !== "" ? resolvedValue : "[value not set]" })

  const edit = () => {
    setSelectedItem(item)
    setView("edit")
  }

  const deleteItem = item => {
    setFields((prev) => [...prev.filter((i) => i !== item)])
  }

  const moveItem = (item, direction) => {

    let fromIndex

    setFields((prev) => {
      fromIndex = _.indexOf(prev, item)
      return prev.filter((i) => i !== item)
    })

    setTimeout(() =>
      setFields((prev) => {
        const toIndex = fromIndex + (direction === "up" ? -1 : 1)
        return [
          ...prev.slice(0, toIndex),
          item,
          ...prev.slice(toIndex),
        ]
      }),
      300)
  }

  return (
    <ListItem disablePadding={true}>
      <Card variant="outlined" sx={{
        minHeight: "min-content",
        width: "448px",
        marginLeft: "-10px",
        position: "relative",
        overflow: "hidden",
        marginBottom: 1,
        bgcolor: 'rgba(0,0,0,0.1)'
      }}>
        <FieldPositionGraphic idx={idx} />
        <CardHeader
          title={title}
          subheader={subtitle}
          slotProps={{
            title: {
              sx: { color: resolvedValue.length ? "lightgray" : "darkgray", fontSize: "14px" }
            },
            subheader: {
              sx: { color: resolvedValue.length ? "secondary.main" : "darkgray", fontSize: "20px" }
            }
          }}
          style={{ cursor: "pointer" }}
          onClick={edit}
        />

        <div className='flex flex-row justify-between'>
          <div><IconButton sx={{ color: "red" }} onClick={() => deleteItem(item)} disabled={item.readonly?.label || item.readonly?.value}><DeleteForeverOutlinedIcon /></IconButton></div>
          <div>
            <IconButton disabled={idx === 0} onClick={() => moveItem(item, "up")}><ArrowCircleUpOutlinedIcon /></IconButton>
            <IconButton disabled={idx === (fields.length - 1)} onClick={() => moveItem(item, "down")}><ArrowCircleDownOutlinedIcon /></IconButton>
          </div>
          <div><IconButton color="primary" onClick={edit}><SettingsOutlinedIcon /></IconButton></div>
        </div>
      </Card>
    </ListItem>
  )
}

const FieldPositionGraphic = ({ idx }) => {

  const { fields, path } = useContext(EditorContext)
  if (path !== "backFields") {
    const items = fields.map((d, item) => <div key={item} className={"item" + (item === idx ? " selected" : "")}></div>)
    return <div className='field-position-graphic'>{items}</div>
  }
}


const EditCard = ({ selectedItem, view, setView }) => {
  const { client } = useContext(AppContext)
  const canEditReadonly = Boolean(client?.is_smartix_account)

  const back = () => setView("list")

  if (selectedItem) return (
    <Slide
      direction="left"
      in={view === "edit"}
      timeout={400}
      mountOnEnter
      unmountOnExit
    >
      <div className='absolute top-0 left-0 w-full'>
        <Paper sx={{ bgcolor: 'background.paper', p: 1, position: "sticky", top: 0, left: 0, width: "100%", zIndex: 50 }} elevation={16}>
          <div className='flex flex-row justify-between'>
            <Button startIcon={<WestRoundedIcon />} onClick={back} >back</Button>
          </div>
        </Paper>

        <div className='overflow-y-scroll'>
          <List>
            <EditCardValueAndLabel item={selectedItem} field="label" />
            <EditCardValueAndLabel item={selectedItem} field="value" />
            {canEditReadonly && <ReadonlySettings item={selectedItem} />}
            <TextAlignment item={selectedItem} />
            <FieldFormatting item={selectedItem} />
            <FieldFormattingSubTypeWrapper item={selectedItem} />
            <DateTimeOptions item={selectedItem} />
            <ChangeMessage item={selectedItem} />
          </List>
        </div>
      </div>
    </Slide>
  )
}

const ReadonlySettings = ({ item }) => {

  const { setFields } = useContext(EditorContext)

  const updateReadonly = (field, value) => {
    setFields(prev => prev.map(f => {
      if (f.key !== item.key) return f
      return {
        ...f,
        readonly: {
          label: f.readonly?.label ?? false,
          value: f.readonly?.value ?? false,
          [field]: value
        }
      }
    }))
  }

  return (
    <Card variant="outlined" sx={{
      minHeight: "min-content",
      width: "100%",
      position: "relative",
      overflow: "hidden",
      marginBottom: 1,
      bgcolor: 'rgba(0,0,0,0.1)'
    }}>
      <CardContent>
        <Typography sx={{ color: "secondary.main", marginBottom: 1 }}>Read Only</Typography>
        <Stack spacing={1}>
          <FormControlLabel
            control={<Switch checked={Boolean(item.readonly?.label)} onChange={e => updateReadonly("label", e.target.checked)} />}
            label="Label is read only"
          />
          <FormControlLabel
            control={<Switch checked={Boolean(item.readonly?.value)} onChange={e => updateReadonly("value", e.target.checked)} />}
            label="Value is read only"
          />
        </Stack>
      </CardContent>
    </Card>
  )
}


const EditCardValueAndLabel = ({ item, field }) => {

  const { setFields, variables, dataSelector, catalogMap } = useContext(EditorContext)

  const initialMode = item[field]?.toString().match(/{{.*}}/) ? "instance" : "template"

  const [mode, setMode] = useState(initialMode)
  const [draftValue, setDraftValue] = useState(item[field] ?? "")
  const isEditingRef = useRef(false)

  useEffect(() => {
    if (!isEditingRef.current) {
      setDraftValue(item[field] ?? "")
    }
  }, [item.key, field, item[field]])

  const handleChangeDataSource = value => {
    setMode(value)
    const needsExpertMode = (value === "instance") && item[field].length !== 0 && !/^{{[^}]*}}$/.test(item[field])

    if (!needsExpertMode) {
      const newValue = (value === "template") ? dataSelector({ template: item, path: field, variables }) : findPathByValue(variables, item[field])
      const newItem = { ...item, [field]: newValue }
      if (value === "template" && field === "value") delete newItem.value_type

      if (field === "value" && !getCatalogPathFromReference(item[field])) {
        const catalogPath = getCatalogPathFromReference(newValue)
        if (catalogPath && catalogMap[catalogPath]) {
          applyFormattingPresetFromCatalog(newItem, catalogMap[catalogPath])
        }
      }

      setFields(prev => {
        let x = prev.map(i => i.key === newItem.key ? newItem : i)
        return x
      })
    }
  }

  return (
    <Card variant="outlined" sx={{
      minHeight: "min-content",
      width: "100%",
      position: "relative",
      overflow: "hidden",
      marginBottom: 1,
      bgcolor: 'rgba(0,0,0,0.1)'
    }}>
      <Typography sx={{ color: "secondary.main", marginLeft: 2, marginTop: 2 }}>{field}</Typography>

      <CardContent>
        <RadioGroup
          row
          aria-labelledby="data_type_label"
          value={mode}
          onChange={e => handleChangeDataSource(e.target.value)}
          className='mb-5'
        >
          <FormControlLabel value="template" control={<Radio disabled={item.readonly?.[field]} />} label="Fixed text" />
          <FormControlLabel value="instance" control={<Radio disabled={item.readonly?.[field]} />} label="Use a variable" />
        </RadioGroup>

        {mode === "template" && <DataSourceTemplate item={item} field={field} draftValue={draftValue} setDraftValue={setDraftValue} isEditingRef={isEditingRef} />}
        {mode === "instance" && <DataSourceInstance item={item} field={field} />}
      </CardContent>

    </Card>
  )
}

const DataSourceTemplate = ({ item, field, draftValue, setDraftValue, isEditingRef }) => {

  const { setFields, path, validationErrors, setValidationErrors } = useContext(EditorContext)
  const validationKey = `${item.key}:${field}`
  const varSubType = field === "value" ? item.type : "text"

  const updateValue = value => {
    setFields(prev => prev.map(f => {
      if (f.key !== item.key) return f
      return { ...f, [field]: value }
    }))
  }


  return (
    <TextField label={field}
      value={draftValue}
      slotProps={{ inputLabel: { shrink: true } }}
      type="text"
      disabled={item.readonly?.[field]}
      onFocus={() => { isEditingRef.current = true }}
      onBlur={() => {
        isEditingRef.current = false
        setDraftValue(item[field] ?? "")
      }}
      onChange={e => {
        const nextValue = e.target.value
        setDraftValue(nextValue)

        const validation = validateValueInput({ input: nextValue, varSubType })
        setValidationErrors(prev => ({
          ...prev,
          [validationKey]: validation.valid ? "" : validation.helperText
        }))

        if (!validation.valid) return
        updateValue(nextValue)
      }}
      error={Boolean(validationErrors?.[validationKey] || !validateValueInput({ input: draftValue, varSubType }).valid)}
      helperText={validationErrors?.[validationKey] || (validateValueInput({ input: draftValue, varSubType }).valid ? "" : validateValueInput({ input: draftValue, varSubType }).helperText)}
      data-validation-key={validationKey}
      fullWidth
      multiline={path === "backFields"}
      maxRows={5}
    />
  )
}


const DataSourceInstance = ({ item, field }) => {
  return <SimpleInstanceSelector active item={item} field={field} />
}

const ValidationSummary = ({ validationErrors, onFocusField }) => {
  const errors = Object.entries(validationErrors).filter(([, message]) => Boolean(message))
  if (!errors.length) return null

  const focusFirstError = () => {
    const [, validationKey] = errors[0]
    onFocusField(validationKey)
    const element = [...document.querySelectorAll('[data-validation-key]')].find(item => item.dataset.validationKey === validationKey)
    element?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    element?.querySelector('input, textarea')?.focus()
  }

  return <Typography color="error" variant="caption" sx={{ whiteSpace: 'nowrap' }}>
    {errors.length} {errors.length === 1 ? 'field has' : 'fields have'} an error. <Button size="small" color="error" onClick={focusFirstError}>View</Button>
  </Typography>
}

const SimpleInstanceSelector = ({ active, item, field }) => {

  const { setFields, sourcePaths, resolveValue, variables, setVariables, dataSelector, path, catalogMap, validationErrors, setValidationErrors, isManagedPassTemplate } = useContext(EditorContext)

  const resolvedValue = useMemo(() => {
    return resolveValue(item, field)
  }, [resolveValue, item, field, variables])
  const resolvedCurrencyCode = useMemo(() => {
    if (item.type !== "money") return item.currencyCode
    return resolveValue(item, "currencyCode") ?? item.currencyCode
  }, [item, resolveValue, variables])

  const variablesPath = item[field]?.toString()?.replace(/[{}]/g, "")
  const variableValue = variablesPath ? _.get(variables, variablesPath) : ""
  const varSubType = catalogMap[variablesPath]?.var_type ?? item.type
  const catalogEntry = catalogMap[variablesPath]
  const isTextareaHint = catalogEntry?.var_ui_hint === "textarea"
  const instanceValueReadonly = item.readonly?.[field]
    || (catalogEntry?.editable !== undefined && Number(catalogEntry?.editable) === 0)
  const validationKey = `${item.key}:${field}`
  const [draftValue, setDraftValue] = useState(variableValue ?? "")

  useEffect(() => {
    setDraftValue(variableValue ?? "")
  }, [variableValue, item.key, field, item[field]])

  useEffect(() => {
    const validation = validateValueInput({ input: draftValue, varSubType })
    const nextError = validation.valid ? "" : validation.helperText

    setValidationErrors(previous => previous[validationKey] === nextError
      ? previous
      : { ...previous, [validationKey]: nextError })
  }, [draftValue, varSubType, validationKey, setValidationErrors])

  const updateValue = value => {
    setFields(prev => prev.map(f => {
      if (f.key !== item.key) return f

      const nextField = { ...f, [field]: value }
      const hadInstanceBinding = Boolean(getCatalogPathFromReference(f[field]))
      const nextInstancePath = getCatalogPathFromReference(value)

      if (
        field === "value" &&
        !hadInstanceBinding &&
        nextInstancePath &&
        catalogMap[nextInstancePath]
      ) {
        applyFormattingPresetFromCatalog(nextField, catalogMap[nextInstancePath])
      }

      return nextField
    }))
  }

  const updateResolvedValue = value => {
    if (instanceValueReadonly) return

    const normalisedValue = varSubType === "choice"
      ? value?.toString().trim().toUpperCase()
      : value

    setDraftValue(normalisedValue)
    const validation = validateValueInput({ input: normalisedValue, varSubType })
    setValidationErrors(prev => ({
      ...prev,
      [validationKey]: validation.valid ? "" : validation.helperText
    }))

    if (!validation.valid) return

    const path = variablesPath

    const variablesCopy = _.cloneDeep(variables)
    _.set(variablesCopy, path, normalisedValue)
    setVariables(variablesCopy)
  }

  const formattedValue = useMemo(
    () => getFormattedValue({
      item: item.type === "money" ? { ...item, currencyCode: resolvedCurrencyCode } : item,
      resolvedValue
    }),
    [
      resolvedValue,
      item.type,
      item.dateStyle,
      item.timeStyle,
      item.numberStyle,
      resolvedCurrencyCode,
      item.isRelative,
      item.ignoresTimeZone
    ]
  )

  if (active) return (
    <>
      <VariablesSelector updateValue={updateValue} sourcePaths={sourcePaths} value={item[field]} dataSelector={dataSelector} variables={variables} readonly={item.readonly?.[field]} />

      <TextField
        label={field}
        value={draftValue}
        slotProps={{ inputLabel: { shrink: true } }}
        disabled={instanceValueReadonly}
        error={Boolean(validationErrors?.[validationKey] || !validateValueInput({ input: draftValue, varSubType }).valid)}
      helperText={validationErrors?.[validationKey] || (validateValueInput({ input: draftValue, varSubType }).valid ? "" : validateValueInput({ input: draftValue, varSubType }).helperText)}
      data-validation-key={validationKey}
      onChange={e => updateResolvedValue(e.target.value)}
        fullWidth
        sx={{ mb: 1 }}
        multiline={path === "backFields" || isTextareaHint}
        maxRows={5}
      />

      <SubtypeValueField label="Resolved value"
        value={resolvedValue ?? ""}
        varSubType={varSubType}
        ignoresTimeZone={item.ignoresTimeZone}
        disabled={true}
        fullWidth
        sx={{ mb: 1 }}
        multiline={path === "backFields" || isTextareaHint}
        maxRows={5}
        onChange={() => { }}
        slotProps={{ inputLabel: { shrink: true } }}
      />

      {["number", "money", "date", "time", "datetime"].includes(item.type) &&
        <TextField label="formatted value"
          value={formattedValue}
          slotProps={{ inputLabel: { shrink: true } }}
          fullWidth
          disabled={true}
          multiline={path === "backFields"}
          maxRows={5}
        />
      }
    </>
  )
}

const SubtypeValueField = ({ value, onChange, varSubType, ignoresTimeZone = false, helperText, error, multiline = false, ...props }) => {
  const dateInputRef = useRef(null)
  const dateTimeInputRef = useRef(null)

  const stringValue = value?.toString() ?? ""

  const endAdornment = (varSubType === "date" || varSubType === "datetime") ? (
    <InputAdornment position='end'>
      {varSubType === "date" ? (
        <>
          <IconButton edge='end' size='small' onClick={() => dateInputRef.current?.showPicker?.() ?? dateInputRef.current?.click()}>
            <EventOutlinedIcon fontSize='small' />
          </IconButton>
          <input
            ref={dateInputRef}
            type='date'
            value={toNativeDateValue(stringValue)}
            onChange={event => onChange(fromNativeDateValue(event.target.value))}
            style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
            tabIndex={-1}
          />
        </>
      ) : null}

      {varSubType === "datetime" ? (
        <>
          <IconButton edge='end' size='small' onClick={() => dateTimeInputRef.current?.showPicker?.() ?? dateTimeInputRef.current?.click()}>
            <ScheduleOutlinedIcon fontSize='small' />
          </IconButton>
          <input
            ref={dateTimeInputRef}
            type='datetime-local'
            step='1'
            value={toNativeDateTimeValue(stringValue, ignoresTimeZone)}
            onChange={event => onChange(fromNativeDateTimeValue(event.target.value))}
            style={{ position: "absolute", width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
            tabIndex={-1}
          />
        </>
      ) : null}
    </InputAdornment>
  ) : null

  if (varSubType === "boolean") {
    return (
      <TextField
        {...props}
        select
        value={stringValue === "" ? "false" : stringValue}
        error={error}
        helperText={helperText}
        onChange={event => onChange(event.target.value)}
      >
        <MenuItem value="false">false</MenuItem>
        <MenuItem value="true">true</MenuItem>
      </TextField>
    )
  }

  if (varSubType === "colour") {
    const pickerColour = normaliseColourInput(stringValue) ?? "#ffffff"

    return (
      <div className='flex flex-col gap-2'>
        <TextField
          {...props}
          value={stringValue}
          error={error}
          helperText={helperText}
          onChange={event => onChange(event.target.value)}
        />
        <HexColorPicker color={pickerColour} onChange={onChange} />
      </div>
    )
  }

  if (varSubType === "choice") {
    return (
      <TextField
        {...props}
        select
        value={stringValue}
        error={error}
        helperText={helperText}
        onChange={event => onChange(event.target.value)}
      >
        <MenuItem value="">Select currency</MenuItem>
        {currencies.map(currency => (
          <MenuItem key={currency.code} value={currency.code}>
            {currency.code} - {currency.name}
          </MenuItem>
        ))}
      </TextField>
    )
  }

  return (
    <TextField
      {...props}
      value={stringValue}
      type={varSubType === "email" ? "email" : varSubType === "phone" ? "tel" : "text"}
      multiline={varSubType === "text" ? multiline : false}
      minRows={varSubType === "text" && multiline ? 3 : undefined}
      error={error}
      helperText={helperText}
      onChange={event => {
        if (varSubType === "integer" && !isIntegerInput(event.target.value)) return
        if (["number", "money"].includes(varSubType) && !isDecimalInput(event.target.value)) return
        if (varSubType === "choice" && !/^[a-zA-Z]{0,3}$/.test(event.target.value)) return
        onChange(varSubType === "choice" ? event.target.value.toUpperCase() : event.target.value)
      }}
      inputProps={{
        inputMode: ["integer", "number", "money"].includes(varSubType) ? "decimal" : undefined,
        maxLength: varSubType === "choice" ? 3 : undefined,
        pattern: varSubType === "integer"
          ? "-?[0-9]*"
          : ["number", "money"].includes(varSubType)
            ? "-?[0-9]*[.]?[0-9]*"
            : varSubType === "choice"
              ? "[A-Za-z]{3}"
              : undefined
      }}
      InputProps={{
        endAdornment
      }}
      sx={{
        "& .MuiInputBase-input::placeholder, & .MuiInputBase-inputMultiline::placeholder": {
          color: "text.disabled",
          opacity: 1
        },
        ...(props.sx ?? {})
      }}
    />
  )
}


const TextAlignment = ({ item }) => {

  const { setFields, path } = useContext(EditorContext)

  if (["primaryFields", "backFields",].includes(path)) return

  const updateValue = value => {
    setFields(prev => prev.map(f => {
      if (f.key === item.key) f.textAlignment = value
      return f
    }))
  }

  return (
    <Card variant="outlined" sx={{
      minHeight: "min-content",
      width: "100%",
      position: "relative",
      overflow: "hidden",
      marginBottom: 1,
      bgcolor: 'rgba(0,0,0,0.1)'
    }}>
      <CardContent>
        <div className='flex flex-row justify-between w-full'>

          <Typography sx={{ color: "secondary.main" }}>Alignment</Typography>

          <ToggleButtonGroup
            value={item.textAlignment}
            exclusive
            onChange={(e, newValue) => updateValue(newValue)}
            aria-labelledby="text_align_label"
          >
            <ToggleButton value="PKTextAlignmentLeft" aria-label="left aligned">
              <FormatAlignLeftIcon />
            </ToggleButton>
            <ToggleButton value="PKTextAlignmentCenter" aria-label="centered">
              <FormatAlignCenterIcon />
            </ToggleButton>
            <ToggleButton value="PKTextAlignmentRight" aria-label="right aligned">
              <FormatAlignRightIcon />
            </ToggleButton>
          </ToggleButtonGroup>
        </div>
      </CardContent>
    </Card>
  )
}

const FieldFormatting = ({ item }) => {

  const { setFields, path, catalogMap } = useContext(EditorContext)
  const allowedTypes = getAllowedAppleFormattingTypes({ item, catalogMap })
  const formattingOptions = [
    ["text", "None (Text)"],
    ["number", "Number"],
    ["date", "Date"],
    ["time", "Time"],
    ["datetime", "Date and Time"],
    ["money", "Money"],
    ...(path === "backFields" ? appleLinkFieldTypes.map(value => [
      value,
      { www: "Website", email: "Email", phone: "Phone", location: "Map Location" }[value]
    ]) : [])
  ].filter(([value]) => allowedTypes.includes(value))

  if (allowedTypes.length <= 1) return null

  const updateValue = value => {
    if (!allowedTypes.includes(value)) return

    setFields(prev => prev.map(f => {

      if (f.key === item.key) {
        applyFormattingDefaults(f, value)
      }

      return f
    }))
  }

  return (
    <Card variant="outlined" sx={{
      minHeight: "min-content",
      width: "100%",
      position: "relative",
      overflow: "hidden",
      marginBottom: 1,
      padding: 1,
      bgcolor: 'rgba(0,0,0,0.1)'
    }}>
      <Typography sx={{ color: "secondary.main", margin: 1 }}>Formatting</Typography>

      <RadioGroup
        row
        aria-labelledby="field_formatting_label"
        value={item.type}
        onChange={e => updateValue(e.target.value)}
      >
        {formattingOptions.map(([value, label]) => (
          <FormControlLabel key={value} value={value} control={<Radio />} label={label} />
        ))}
      </RadioGroup>
    </Card>)
}


const FieldFormattingSubTypeWrapper = ({ item }) => {

  if (item.type !== "text") return (
    <Card variant="outlined" sx={{
      minHeight: "min-content",
      width: "100%",
      position: "relative",
      overflow: "hidden",
      marginBottom: 1,
      padding: 1,
      bgcolor: 'rgba(0,0,0,0.1)'
    }}>
      <FieldFormattingSubType item={item} />
    </Card>
  )
}

const FieldFormattingSubType = ({ item }) => {

  const { setFields } = useContext(EditorContext)

  const updateValue = (field, value) => {
    setFields(prev => prev.map(f => {
      if (f.key === item.key) f[field] = value
      return f
    }))
  }

  switch (item.type) {
    case "number": {
      return (
        <>
          <Typography sx={{ color: "secondary.main", margin: 1 }}>Number format</Typography>
          <RadioGroup
            value={item.numberStyle}
            onChange={e => updateValue("numberStyle", e.target.value)}
          >
            <FormControlLabel value="PKNumberStyleDecimal" control={<Radio />} label="Decimal" />
            <FormControlLabel value="PKNumberStylePercent" control={<Radio />} label="Percentage" />
            <FormControlLabel value="PKNumberStyleScientific" control={<Radio />} label="Scientific" />
            <FormControlLabel value="PKNumberStyleSpellOut" control={<Radio />} label="Spell Out" />
          </RadioGroup>
        </>
      )
    }

    case "date": {
      return (
        <>
          <Typography sx={{ color: "secondary.main", margin: 1 }}>Date format</Typography>
          <RadioGroup
            value={item.dateStyle}
            onChange={e => updateValue("dateStyle", e.target.value)}
          >
            <FormControlLabel value="PKDateStyleNone" control={<Radio />} label="None (hide date)" />
            <FormControlLabel value="PKDateStyleShort" control={<Radio />} label="Short (DD/MM/YY)" />
            <FormControlLabel value="PKDateStyleMedium" control={<Radio />} label="Medium (DD MMM YYYY)" />
            <FormControlLabel value="PKDateStyleLong" control={<Radio />} label="Long (DD MMMM YYYY)" />
            <FormControlLabel value="PKDateStyleFull" control={<Radio />} label="Full (dddd DD MMMM YYYY)" />
          </RadioGroup>
        </>
      )
    }

    case "time": {
      return (
        <>
          <Typography sx={{ color: "secondary.main", margin: 1 }}>Time format</Typography>
          <RadioGroup
            value={item.timeStyle}
            onChange={e => updateValue("timeStyle", e.target.value)}
          >
            <FormControlLabel value="PKDateStyleNone" control={<Radio />} label="None (hide time)" />
            <FormControlLabel value="PKDateStyleShort" control={<Radio />} label="Short (HH:MM)" />
            <FormControlLabel value="PKDateStyleMedium" control={<Radio />} label="Medium (HH:MM:SS)" />
            <FormControlLabel value="PKDateStyleLong" control={<Radio />} label="Long (HH:MM:SS GMT)" />
            <FormControlLabel value="PKDateStyleFull" control={<Radio />} label="Full (HH:MM:SS Greenwich Mean Time)" />
          </RadioGroup>
        </>
      )
    }

    case "datetime": {
      return (
        <div className='flex flex-col gap-10 w-full'>
          <div>
            <Typography sx={{ color: "secondary.main", margin: 1 }}>Date format</Typography>
            <RadioGroup
              value={item.dateStyle}
              onChange={e => updateValue("dateStyle", e.target.value)}
            >
              <FormControlLabel value="PKDateStyleNone" control={<Radio />} label="None (hide date)" />
              <FormControlLabel value="PKDateStyleShort" control={<Radio />} label="Short (DD/MM/YY)" />
              <FormControlLabel value="PKDateStyleMedium" control={<Radio />} label="Medium (DD MMM YYYY)" />
              <FormControlLabel value="PKDateStyleLong" control={<Radio />} label="Long (DD MMMM YYYY)" />
              <FormControlLabel value="PKDateStyleFull" control={<Radio />} label="Full (dddd DD MMMM YYYY)" />
            </RadioGroup>
          </div>

          <div>
            <Typography sx={{ color: "secondary.main", margin: 1 }}>Time format</Typography>
            <RadioGroup
              value={item.timeStyle}
              onChange={e => updateValue("timeStyle", e.target.value)}
            >
              <FormControlLabel value="PKDateStyleNone" control={<Radio />} label="None (hide time)" />
              <FormControlLabel value="PKDateStyleShort" control={<Radio />} label="Short (HH:MM)" />
              <FormControlLabel value="PKDateStyleMedium" control={<Radio />} label="Medium (HH:MM:SS)" />
              <FormControlLabel value="PKDateStyleLong" control={<Radio />} label="Long (HH:MM:SS GMT)" />
              <FormControlLabel value="PKDateStyleFull" control={<Radio />} label="Full (HH:MM:SS Greenwich Mean Time)" />
            </RadioGroup>
          </div>
        </div>
      )
    }

    case "money": {
      return (
        <>
          <Typography sx={{ color: "secondary.main", margin: 1 }}>Currency</Typography>
          <TextField
            select
            value={item.currencyCode ?? ""}
            onChange={e => updateValue("currencyCode", e.target.value)}
            fullWidth
          >
            <MenuItem value="">Select currency</MenuItem>
            {currencies.map(currency => (
              <MenuItem key={currency.code} value={currency.code}>
                {currency.code} - {currency.name}
              </MenuItem>
            ))}
          </TextField>
        </>
      )
    }
  }
}


const DateTimeOptions = ({ item }) => {

  const { setFields } = useContext(EditorContext)

  const updateValue = (field, value) => {
    setFields(prev => prev.map(f => {
      if (f.key === item.key) f[field] = value
      return f
    }))
  }

  useEffect(() => {
    if (["datetime", "date", "time"].includes(item.type)) {
      if (item.type === "time") {
        if (!!!item.ignoresTimeZone) updateValue("ignoresTimeZone", false)
      } else { // date / datetime
        if (!!!item.isRelative) updateValue("isRelative", false)
      }
    }
  }, [item])

  if (["datetime", "date", "time"].includes(item.type)) {

    return (
      <Card variant="outlined" sx={{
        minHeight: "min-content",
        width: "100%",
        position: "relative",
        overflow: "hidden",
        marginBottom: 1,
        padding: 1,
        bgcolor: 'rgba(0,0,0,0.1)'
      }}>
        <Typography sx={{ color: "secondary.main", margin: 1 }}>Date/time options</Typography>

        <RadioGroup
          row
          aria-labelledby="field_formatting_label"
          value={String(item.ignoresTimeZone ?? true)}
          onChange={e => updateValue("ignoresTimeZone", e.target.value === "true")}
        >
          <FormControlLabel value="true" control={<Radio />} label="Ignore time zone" />
          <FormControlLabel value="false" control={<Radio />} label="Adjust to user's time zone" />
        </RadioGroup>

        {["datetime", "date"].includes(item.type) &&
          <RadioGroup
            row
            aria-labelledby="field_formatting_label"
            value={item.isRelative}
            onChange={e => updateValue("isRelative", e.target.value)}
          >
            <FormControlLabel value={false} control={<Radio />} label={`Display as absolute date`} />
            <FormControlLabel value={true} control={<Radio />} label={`Display as relative date`} />
          </RadioGroup>
        }
      </Card>
    )
  }
}


const ChangeMessage = ({ item }) => {

  const { setFields } = useContext(EditorContext)

  const updateValue = value => {
    setFields(prev => prev.map(f => {
      if (f.key === item.key) f.changeMessage = value
      return f
    }))
  }

  const mode = item.value?.toString().match(/{{.*}}/) ? "instance" : "template"

  if (mode === "instance") return (
    <Card variant="outlined" sx={{
      minHeight: "min-content",
      width: "100%",
      position: "relative",
      overflow: "hidden",
      bgcolor: 'rgba(0,0,0,0.1)'
    }}>
      <CardContent>
        <Typography sx={{ color: "secondary.main", marginBottom: 1 }}>Change Message</Typography>
        <TextField
          value={item.changeMessage}
          type="text"
          onChange={e => { updateValue(e.target.value) }}
          fullWidth
        />
        <p className='mt-2'><b>NOTE:</b> Must contain <b>%@</b>. This will be substituted with the new value.</p>
        <p className='mt-2'>eg. "Date changed to %@" will appear as "Date changed to 12 May 2026" if the new value is "12 May 2026"</p>
        <p className='mt-2'>Apple will display a generic change message if you do not include <b>%@</b>.</p>

      </CardContent>
    </Card>
  )
}
